/*
 * [2026-09-07] 유저페이지 로그인 만료·무활동 자동 로그아웃 공통 규칙
 *
 * 정본: ~/tosino/docs/SPEC_자동로그아웃_세션만료.md §3 "유저페이지"
 *
 * WHY(실사고): 토큰이 만료돼 서버가 401 을 보내도, 홈·슬롯·카지노처럼 비로그인도 볼 수 있는
 *   화면에 있으면 화면이 아무 처리도 하지 않았다. 그래서 상단바는 로그인 상태로 남고 잔액·쪽지·
 *   게임 실행만 조용히 전부 실패했다(lime 하루 401 1,718건). 만료는 "요청이 실패했다"가 아니라
 *   "로그인이 끝났다"이므로, 어느 화면에 있든 로그인 정보를 지우고 로그인 화면으로 보낸다.
 *
 * 이 파일이 규칙의 유일한 자리다. 화면·서비스는 여기 함수만 부른다.
 *   - 401 판정·정리·이동      : handleUnauthorized (services/api.js 응답 인터셉터)
 *   - 마지막 사람 입력 시각    : readLastActivity / stampActivity (요청 헤더 X-Last-Activity)
 *   - 만료 예약 · 무활동 감시  : startSessionGuard (앱 진입점에서 1회)
 *   - 게임 세션 열림/닫힘      : markGameSessionOpen / markGameSessionClosed (services/games.js)
 *
 * ⚠️ 숫자·키 이름·문구는 전부 아래 상수에만 둔다. 화면·서비스에 다시 적지 말 것.
 */

/* WHY(정적 import 는 axios 만): services/api.js 가 이 파일을 부르므로 그 모듈은 아래에서
   지연 로딩한다(순환 참조 회피). axios 는 이 파일을 부르지 않아 정적으로 가져와도 안전하다. */
import axios from 'axios';

/* ── 서버와 맞춘 계약값 (바꾸면 양쪽을 함께 바꿔야 한다) ── */
export const ACTIVITY_HEADER = 'X-Last-Activity';        // 요청 헤더 이름(epoch ms)
export const LAST_ACTIVITY_KEY = 'lastUserActivity';     // localStorage 키(탭 간 공유)
export const SESSION_POLICY_PATH = '/auth/session-policy'; // { userIdleMinutes }
export const SESSION_LOGOUT_PATH = '/auth/logout';
export const LOGIN_PATH = '/login';

/* 서버가 보내는 401 코드 */
export const SESSION_ERROR_CODES = {
  SESSION_EXPIRED: 'SESSION_EXPIRED', // 다른 곳에서 로그인(중복 로그인)
  IDLE_TIMEOUT: 'IDLE_TIMEOUT',       // 무활동 자동 로그아웃
  TOKEN_EXPIRED: 'TOKEN_EXPIRED'      // 로그인 만료(서버 시계 기준 판정 — 이 코드가 정본)
};

/* 로그인 정보로 저장되는 키 전부 — 만료·강제 로그아웃 때 함께 지운다.
   WHY: 하나라도 남으면 다음 화면이 "로그인된 것처럼" 그려져 같은 증상이 되돌아온다. */
export const AUTH_STORAGE_KEYS = ['token', 'userId', 'userBalance', 'userPoints', 'user'];

/* 401 을 받아도 로그인 화면으로 보내지 않는 요청.
   로그인·회원가입 실패는 그 화면이 사유를 보여 주고, 인증상태 조회의 401 은 정상 응답이다. */
export const AUTH_EXEMPT_PATHS = ['/auth/login', '/auth/register', '/auth/status'];

/* 게임 세션이 화면 새로고침을 넘어 살아 있는지 판단하는 저장 키.
   pages/GameplayPage.jsx 가 게임 실행 시 같은 키에 실행 주소를 넣는다(그 파일의 문자열은 이 변경 이전부터 있던 것). */
export const GAME_SESSION_STORAGE_KEY = 'activeGameUrl';

/* 동작값 */
export const IDLE_WARNING_LEAD_MS = 60 * 1000; // 자동 로그아웃 60초 전 안내
export const ACTIVITY_THROTTLE_MS = 200;       // 입력 이벤트 기록 최소 간격
export const WATCH_TICK_MS = 1000;             // 만료·무활동 확인 주기
export const POLICY_REFRESH_MS = 10 * 60 * 1000; // 무활동 설정 재조회 주기
export const POLICY_MIN_INTERVAL_MS = 5 * 1000;      // 같은 설정을 잇달아 다시 묻지 않는 최소 간격
export const LOGOUT_CALL_TIMEOUT_MS = 3000;    // 로그아웃 통보(best-effort) 대기 한도

/* 안내 문구 */
export const MESSAGES = {
  expired: '로그인이 만료되었습니다. 다시 로그인해주세요.',
  idle: '오랫동안 활동이 없어 자동 로그아웃되었습니다. 다시 로그인해주세요.',
  duplicate: '다른 곳에서 로그인되었습니다.',
  idleWarning: (seconds) => `${seconds}초 후 자동 로그아웃됩니다.`
};

const WARNING_ELEMENT_ID = 'session-idle-warning';

/* ══════════════════════════════════════════════════════════
   1. 순수 함수 — 저장소·화면을 건드리지 않는다(단위 테스트 대상)
   ══════════════════════════════════════════════════════════ */

/* JWT 의 만료 시각(ms)을 읽는다. 형식이 아니거나 exp 가 없으면 null(= 예약하지 않음). */
export function decodeJwtExpiryMs(token) {
  if (typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    // base64url → base64 로 바꾸고 길이를 4의 배수로 맞춘다(atob 는 표준 base64 만 받는다).
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(
      typeof atob === 'function'
        ? decodeURIComponent(
            atob(padded)
              .split('')
              .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
              .join('')
          )
        : Buffer.from(padded, 'base64').toString('utf8')
    );
    if (!payload || typeof payload.exp !== 'number' || !Number.isFinite(payload.exp)) return null;
    return payload.exp * 1000;
  } catch (e) {
    return null;
  }
}

/* 로그인·회원가입·인증상태 조회인가(= 401 이 와도 이동하지 않는 요청인가) */
export function isAuthExemptRequest(requestUrl) {
  const url = typeof requestUrl === 'string' ? requestUrl : '';
  return AUTH_EXEMPT_PATHS.some((path) => url.includes(path));
}

/* 이 401 로 로그인 화면에 보내야 하는가.
   요청 당시 토큰이 있었고(=로그인 상태였고) 면제 요청이 아니면 보낸다.
   토큰 없이 받은 401(비로그인 방문자의 공개 조회 등)은 종전처럼 무시한다.
   ⚠️ 화면 종류(publicPaths)·API 종류(publicAPIs)는 더 이상 이동 여부를 좌우하지 않는다. */
export function shouldForceLogin({ hadToken, requestUrl }) {
  if (!hadToken) return false;
  return !isAuthExemptRequest(requestUrl);
}

/* 401 응답에서 회원에게 보여 줄 문구를 고른다(없으면 null = 알림 없이 이동).
   사유별로 문구의 출처가 다르다(2026-09-07 운영자 확정 · 정본 SPEC §4):
     - 중복 로그인(SESSION_EXPIRED) = 종전 문구 그대로. 기존 흐름을 문구까지 바꾸지 않는다.
     - 무활동(IDLE_TIMEOUT)        = 서버 문구. 설정한 시간이 사유에 들어가므로 서버가 문구의 주인이다.
     - 로그인 만료(TOKEN_EXPIRED)   = 만료 문구. 예약 타이머가 쓰는 문구와 같아야 한다.
   ⚠️ 만료 판정은 **서버 코드가 정본**이다. 기기 시계로 푼 exp(tokenExpired)는 서버가 코드를
      안 보낸 경우의 폴백으로만 쓴다 — 시계가 틀어진 기기에서 화면이 혼자 만료를 단정하면 안 된다. */
export function resolveUnauthorizedNotice(responseData, { tokenExpired = false } = {}) {
  const code = responseData && responseData.code;
  const serverMessage = responseData && typeof responseData.error === 'string' ? responseData.error : '';
  if (code === SESSION_ERROR_CODES.SESSION_EXPIRED) {
    return { code, message: MESSAGES.duplicate };
  }
  if (code === SESSION_ERROR_CODES.IDLE_TIMEOUT) {
    return { code, message: serverMessage || MESSAGES.idle };
  }
  if (code === SESSION_ERROR_CODES.TOKEN_EXPIRED) {
    return { code, message: MESSAGES.expired };
  }
  // 코드 없는 401 은 기기 시계로 봤을 때 만료인 경우에만 만료 안내를 띄운다(그 외는 조용히 이동).
  return { code: null, message: tokenExpired ? MESSAGES.expired : null };
}

/* 무활동 상태 계산. lastActivityAt 이 없거나 미래면 지금을 기준으로 본다(즉시 로그아웃 방지). */
export function computeIdleState({ now, lastActivityAt, idleMinutes, warnLeadMs = IDLE_WARNING_LEAD_MS }) {
  const minutes = Number(idleMinutes);
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return { enabled: false, expired: false, warning: false, remainingMs: Infinity };
  }
  const base = Number.isFinite(lastActivityAt) && lastActivityAt > 0 && lastActivityAt <= now ? lastActivityAt : now;
  const remainingMs = minutes * 60 * 1000 - (now - base);
  return {
    enabled: true,
    expired: remainingMs <= 0,
    warning: remainingMs > 0 && remainingMs <= warnLeadMs,
    remainingMs
  };
}

/* 응답에서 무활동 설정(분)을 읽는다. 모르는 형식·실패는 0(기능 꺼짐 = 종전 동작). */
export function parseIdleMinutes(responseData) {
  const raw =
    (responseData && responseData.userIdleMinutes) ??
    (responseData && responseData.data && responseData.data.userIdleMinutes);
  const minutes = Number(raw);
  if (!Number.isFinite(minutes) || minutes <= 0) return 0;
  return minutes;
}

/* ══════════════════════════════════════════════════════════
   2. 저장소 접근
   ══════════════════════════════════════════════════════════ */

const hasWindow = () => typeof window !== 'undefined';

function safeLocalGet(key) {
  try {
    return hasWindow() ? window.localStorage.getItem(key) : null;
  } catch (e) {
    return null;
  }
}

function safeLocalSet(key, value) {
  try {
    if (hasWindow()) window.localStorage.setItem(key, value);
  } catch (e) { /* 저장 실패는 무시 — 기능만 꺼진다 */ }
}

function safeLocalRemove(key) {
  try {
    if (hasWindow()) window.localStorage.removeItem(key);
  } catch (e) { /* 무시 */ }
}

export function readToken() {
  return safeLocalGet('token');
}

/* 마지막 사람 입력 시각(epoch ms). 없으면 null. */
export function readLastActivity() {
  const raw = safeLocalGet(LAST_ACTIVITY_KEY);
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : null;
}

/* 마지막 사람 입력 시각을 기록한다(탭 간 공유). */
export function stampActivity(timestamp) {
  const value = Number.isFinite(timestamp) ? timestamp : Date.now();
  safeLocalSet(LAST_ACTIVITY_KEY, String(value));
  return value;
}

/* 로그인 정보 + 활동 기록 정리. 만료·강제 로그아웃·정상 로그아웃이 모두 이 함수를 쓴다. */
export function clearAuthStorage() {
  AUTH_STORAGE_KEYS.forEach(safeLocalRemove);
  safeLocalRemove(LAST_ACTIVITY_KEY);
  gameSessionOpen = false;
}

/* ══════════════════════════════════════════════════════════
   3. 게임 세션 (무활동 타이머 정지 조건)
   ══════════════════════════════════════════════════════════ */

let gameSessionOpen = false;

/* 게임·로비 실행이 성공했다 → 게임 중 */
export function markGameSessionOpen() {
  gameSessionOpen = true;
}

/* 게임이 끝난 것이 확인됐다 → 게임 중 해제.
   ⚠️ 끝났는지 확신할 수 없는 종료 신호(confirmed=false)로는 해제하지 않는다.
      게임 창은 살아 있는데 우리 화면만 벗어난 경우라, 해제하면 실제로 게임 중인 회원이
      무활동으로 로그아웃된다.
   게임 중이었다면 **끝난 시각을 활동으로 한 번 기록한다**(서버의 무활동 규칙과 같다).
   WHY: 게임 중에는 활동 시각이 게임을 켠 시점에 멈춰 있다. 설정한 시간보다 오래 게임한 회원이
     게임을 끝내는 순간 그 멈춘 시각으로 판정되어 다음 1초 확인에서 곧바로 로그아웃됐다.
     게임을 끝낸 것 자체가 사람의 조작이므로 그 시각부터 다시 센다.
   게임 중이 아니었으면 아무것도 기록하지 않는다(종료 신호만으로 활동을 만들지 않는다). */
export function markGameSessionClosed() {
  const wasOpen = isGameSessionOpen();
  gameSessionOpen = false;
  if (wasOpen) stampActivity(Date.now());
}

/* 지금 게임 중인가. 화면 새로고침으로 위 표시가 날아가도 게임 화면이 저장해 둔 실행 주소로 알아본다. */
export function isGameSessionOpen() {
  if (gameSessionOpen) return true;
  try {
    return hasWindow() && !!window.sessionStorage.getItem(GAME_SESSION_STORAGE_KEY);
  } catch (e) {
    return false;
  }
}

/* ══════════════════════════════════════════════════════════
   4. 만료 처리 · 무활동 감시 (실행부)
   ══════════════════════════════════════════════════════════ */

let started = false;
let terminating = false;      // 만료 처리 1회만 — 알림 중복·이동 중복 방지
let watchTimer = null;
let policyTimer = null;
let idleMinutes = 0;
let idleLogoutInFlight = false; // 무활동 로그아웃 통보가 나가 있는 중인지
let policyFetchedAt = 0;        // 무활동 설정을 마지막으로 물어본 시각
let lastActivityWrite = 0;
let apiClientLoader = null;   // services/api.js 지연 로딩(순환 참조 회피)

/* 통신 인스턴스를 늦게 가져온다.
   WHY: services/api.js 가 이 파일을 부르므로 위쪽에서 정적으로 가져오면 순환 참조가 된다. */
function getApiClient() {
  if (!apiClientLoader) {
    apiClientLoader = import('../services/api').then((mod) => mod.default).catch(() => null);
  }
  return apiClientLoader;
}

function redirectToLogin() {
  if (!hasWindow()) return;
  try {
    if (window.location.pathname !== LOGIN_PATH) {
      window.location.replace(LOGIN_PATH);
    }
  } catch (e) { /* 무시 */ }
}

/* 로그인 종료 처리: 타이머 정지 → 저장 정보 정리 → 안내 → 로그인 화면. */
function terminateSession(message, reason) {
  if (terminating) return;
  terminating = true;
  /* [2026-09-07] 왜 로그인 화면으로 갔는지 사후에 알 수 있게 한 줄 남긴다(개인정보 미포함). */
  try { console.warn(`[세션] 로그인 종료 처리 — 사유: ${reason || '알 수 없음'}`); } catch (e) { /* 무시 */ }
  stopTimers();
  hideIdleWarning();
  clearAuthStorage();
  if (message) {
    try { window.alert(message); } catch (e) { /* 무시 */ }
  }
  redirectToLogin();
}

/* services/api.js 응답 인터셉터가 부르는 401 처리. 처리했으면 true. */
export function handleUnauthorized({ hadToken, requestUrl, data }) {
  if (!shouldForceLogin({ hadToken, requestUrl })) return false;
  const token = readToken();
  /* [2026-09-07] 저장소에 로그인 정보가 이미 없으면 조용히 반환한다(안내·이동 없음).
     WHY(tosino ur2 실측): 만료로 로그인 화면에 넘어간 **직후 새 문서**에서, 넘어가기 전에 이미
       나가 있던 요청의 401 이 뒤늦게 처리됐다. 새 문서라 종료 표시가 초기화돼 있어 막지 못했고,
       같은 만료 안내가 한 번 더 떴다. 지울 것이 남아 있지 않다는 것은 이미 처리됐다는 뜻이다.
     정상 로그아웃 직후 늦게 도착하는 401 도 같은 이유로 조용히 지나간다. */
  if (!token) return false;
  const expiresAt = decodeJwtExpiryMs(token);
  const tokenExpired = expiresAt !== null && Date.now() >= expiresAt;
  const notice = resolveUnauthorizedNotice(data, { tokenExpired });
  terminateSession(notice.message, notice.code || (tokenExpired ? '토큰 만료' : '인증 거부(401)'));
  return true;
}

/* 무활동 로그아웃: 서버에 로그아웃을 알리고(실패해도 진행) 정리·이동.
   ⚠️ 통보 응답을 기다리는 동안에도 1초 확인은 계속 돌므로, 진행 중 표시가 없으면
      /auth/logout 이 여러 번 나간다. */
function logoutForIdle() {
  if (terminating || idleLogoutInFlight) return;
  idleLogoutInFlight = true;
  const finish = () => terminateSession(MESSAGES.idle, '무활동 자동 로그아웃');
  getApiClient()
    .then((client) => {
      if (!client) return null;
      return client.post(SESSION_LOGOUT_PATH, {}, { timeout: LOGOUT_CALL_TIMEOUT_MS });
    })
    .catch(() => null)
    .then(finish, finish);
}

/* ── 무활동 안내 배너 ──
   WHY: alert 는 화면을 멈춰 세워 "마우스를 움직이면 사라지는" 안내로 쓸 수 없다.
        틀(블랙·루미나)마다 스낵바가 달라 인라인 스타일 배너 하나로 통일한다. */
function showIdleWarning(remainingMs) {
  if (!hasWindow() || !document.body) return;
  const seconds = Math.max(1, Math.ceil(remainingMs / 1000));
  let el = document.getElementById(WARNING_ELEMENT_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = WARNING_ELEMENT_ID;
    el.setAttribute('role', 'status');
    el.style.cssText = [
      'position:fixed', 'top:16px', 'left:50%', 'transform:translateX(-50%)',
      'z-index:2147483000', 'padding:10px 18px', 'border-radius:8px',
      'background:rgba(20,20,20,0.92)', 'color:#f5c451',
      'border:1px solid rgba(245,196,81,0.5)',
      'font-size:14px', 'font-weight:600', 'letter-spacing:-0.2px',
      'box-shadow:0 4px 16px rgba(0,0,0,0.35)', 'pointer-events:none',
      'max-width:90vw', 'text-align:center'
    ].join(';');
    document.body.appendChild(el);
  }
  const text = MESSAGES.idleWarning(seconds);
  if (el.textContent !== text) el.textContent = text;
}

function hideIdleWarning() {
  if (!hasWindow()) return;
  const el = document.getElementById(WARNING_ELEMENT_ID);
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

/* 토큰 만료 시각은 토큰이 바뀔 때만 다시 푼다(1초마다 base64 해독을 반복하지 않기 위함).
   함께 판정하는 것: 이 토큰을 **처음 봤을 때 이미 만료로 보였는가**.
   ⚠️ 그런 토큰은 화면이 만료를 단정하지 않는다(예약 생략) — 서버의 401 이 확정하게 둔다.
   WHY: 기기 시계가 앞선 단말에서는 방금 받은 정상 토큰도 이미 만료로 보인다. 그때 화면이
     혼자 로그아웃하면 로그인 → 즉시 만료 → 로그인 화면이 끝없이 반복된다(로그인 루프).
     화면이 여는 동안 exp 를 넘어가는 정상적인 경우는 종전대로 예약이 잡는다(같은 시계 안의
     상대 비교라 시계가 틀어져 있어도 일관적이다). */
let cachedToken = null;
let cachedExpiry = null;
let expiryWatchSkipped = false;
function expiryOf(token) {
  if (token !== cachedToken) {
    cachedToken = token;
    cachedExpiry = decodeJwtExpiryMs(token);
    expiryWatchSkipped = cachedExpiry !== null && Date.now() >= cachedExpiry;
    if (expiryWatchSkipped) {
      try {
        console.warn('[세션] 받은 토큰이 이 기기 시계로는 이미 만료 — 화면 만료 예약을 생략하고 서버 응답을 따른다');
      } catch (e) { /* 무시 */ }
    }
  }
  return cachedExpiry;
}

/* 만료·무활동 확인(1초). 잠자기에서 깬 뒤에도 어긋나지 않도록 예약이 아니라 시계로 비교한다. */
function tick() {
  const token = readToken();
  if (!token) { hideIdleWarning(); return; }

  const now = Date.now();
  const expiresAt = expiryOf(token);
  if (expiresAt !== null && !expiryWatchSkipped && now >= expiresAt) {
    terminateSession(MESSAGES.expired, '토큰 만료 시각 도달');
    return;
  }

  if (idleMinutes <= 0) { hideIdleWarning(); return; }

  /* 게임 중에는 화면의 무활동 시계를 세지 않는다(게임 창의 입력은 이 화면에 오지 않는다).
     ⚠️ 활동 시각(lastUserActivity)은 **밀어 주지 않는다**(2026-09-07 운영자 결정).
     WHY: 게임 중 예외의 정본은 서버다 — 유저 API 가 무활동 초과 시 진행 중인 게임 세션이 있으면
       그대로 통과시킨다. 화면까지 활동 시각을 갱신하면, 화면이 "게임 중"이라고 잘못 믿는 동안
       (팝업 차단으로 닫힘 감지가 안 걸린 경우, 게임 창을 켠 채 메뉴만 옮긴 뒤 창을 닫은 경우)
       서버 판정까지 영원히 통과해 무활동 로그아웃이 그 탭에서 다시는 동작하지 않는다.
       갱신하지 않으면 그 고착 상태에서도 서버가 401 IDLE_TIMEOUT 으로 끊어 401 경로로 정리된다. */
  if (isGameSessionOpen()) {
    hideIdleWarning();
    return;
  }

  const state = computeIdleState({ now, lastActivityAt: readLastActivity(), idleMinutes });
  if (state.expired) { logoutForIdle(); return; }
  if (state.warning) showIdleWarning(state.remainingMs); else hideIdleWarning();
}

function startTimers() {
  if (!hasWindow()) return;
  if (watchTimer === null) watchTimer = window.setInterval(tick, WATCH_TICK_MS);
  if (policyTimer === null) policyTimer = window.setInterval(() => loadIdlePolicy({ force: true }), POLICY_REFRESH_MS);
}

/* 예약·감시 타이머 정리(로그아웃·만료 시). */
export function stopTimers() {
  if (!hasWindow()) return;
  if (watchTimer !== null) { window.clearInterval(watchTimer); watchTimer = null; }
  if (policyTimer !== null) { window.clearInterval(policyTimer); policyTimer = null; }
}

/* 무활동 설정 조회(무인증). 실패하면 0 = 기능 꺼짐(종전 동작).
   로그인 한 번에 여러 신호(로그인 응답·인증 상태 변경·다른 탭 알림)가 겹쳐 들어오므로
   방금 물어본 설정은 다시 묻지 않는다(서버도 60초 캐시라 답이 같다). */
function loadIdlePolicy({ force = false } = {}) {
  if (!readToken()) { idleMinutes = 0; policyFetchedAt = 0; return; }
  const now = Date.now();
  if (!force && policyFetchedAt && now - policyFetchedAt < POLICY_MIN_INTERVAL_MS) return;
  policyFetchedAt = now;
  getApiClient()
    .then((client) => (client ? client.get(SESSION_POLICY_PATH) : null))
    .then((response) => {
      idleMinutes = parseIdleMinutes(response && response.data);
      if (idleMinutes <= 0) hideIdleWarning();
    })
    .catch(() => { idleMinutes = 0; policyFetchedAt = 0; });
}

/* 사람의 입력 기록(폴링은 활동이 아니다 — 자동 요청에서는 부르지 않는다). */
function recordActivity() {
  const now = Date.now();
  if (now - lastActivityWrite < ACTIVITY_THROTTLE_MS) return;
  lastActivityWrite = now;
  if (!readToken()) return;
  stampActivity(now);
  hideIdleWarning();
}

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel', 'scroll', 'click'];

function attachActivityListeners() {
  ACTIVITY_EVENTS.forEach((type) => {
    window.addEventListener(type, recordActivity, { passive: true });
  });
  window.addEventListener('focus', recordActivity);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') recordActivity();
  });
}

/* 다른 탭에서 로그인·로그아웃하면 이 탭도 따라간다(활동 시각은 매 확인 때 저장소에서 읽으므로 자동 공유). */
function attachStorageListener() {
  window.addEventListener('storage', (event) => {
    if (event && event.key === 'token') refreshSession({ stamp: false });
  });
}

/* 로그인 상태가 바뀌었을 때 감시를 다시 세운다. */
export function refreshSession({ stamp = false } = {}) {
  if (!hasWindow()) return;
  const token = readToken();
  if (!token) {
    idleMinutes = 0;
    stopTimers();
    hideIdleWarning();
    return;
  }
  terminating = false;
  idleLogoutInFlight = false;
  /* stamp=true 는 사람이 방금 조작한 경우(로그인·로그아웃)에만 온다.
     화면을 새로 여는 것(새로고침·탭 복원)은 활동으로 치지 않는다 — 그렇지 않으면
     30분 방치한 탭이 되살아나며 무활동 시계가 0으로 돌아간다. 기록이 아예 없을 때만 채운다. */
  if (stamp || readLastActivity() === null) stampActivity(Date.now());
  startTimers();
  loadIdlePolicy();
  tick(); // 이미 만료된 토큰으로 화면을 연 경우 즉시 처리
}

/* 기본 axios 인스턴스(팝업·도메인 권한 조회)에도 활동 시각 헤더를 붙인다.
   axios.create 로 만든 인스턴스는 이 인터셉터를 물려받지 않으므로 중복되지 않는다. */
function attachDefaultAxiosHeader() {
  getApiClient(); // services/api.js 를 먼저 올려 두어 첫 요청이 지연되지 않게 한다
  if (!axios || !axios.interceptors) return;
  axios.interceptors.request.use((config) => {
    const lastActivity = readLastActivity();
    if (lastActivity) {
      config.headers = config.headers || {};
      config.headers[ACTIVITY_HEADER] = String(lastActivity);
    }
    return config;
  });
}

/* 앱 진입점에서 1회 호출. 두 번 불러도 한 번만 걸린다. */
export function startSessionGuard() {
  if (started || !hasWindow()) return;
  started = true;
  attachActivityListeners();
  attachStorageListener();
  attachDefaultAxiosHeader();
  window.addEventListener('authStateChanged', () => refreshSession({ stamp: true }));
  refreshSession({ stamp: false });
}

/* 정상 로그아웃 시 정리(services/auth.js·socket 강제 로그아웃에서 호출). */
export function stopSessionGuard() {
  idleMinutes = 0;
  idleLogoutInFlight = false;
  policyFetchedAt = 0;
  cachedToken = null;
  cachedExpiry = null;
  expiryWatchSkipped = false;
  stopTimers();
  hideIdleWarning();
  markGameSessionClosed();
  safeLocalRemove(LAST_ACTIVITY_KEY);
}
